import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconDots,
  IconEye,
  IconPencil,
  IconSearch,
  IconTrash,
  IconUserCheck,
  IconUserShare,
  IconUserX,
} from "@tabler/icons-react"
import { useState } from "react"

interface UserItem {
  id: number
  name: string
  email: string
  role: string
  active: boolean
  avatar_url: string | null
  hire_date: string | null
  seniority_keyword: string | null
  seniority_emoji: string | null
  current_salary_display: string | null
  balance_count: number
  request_count: number
  is_current_user: boolean
  is_admin: boolean
}

interface StatusCounts {
  all: number
  admin: number
  employee: number
}

interface Props {
  [key: string]: unknown
  users: UserItem[]
  pagy: PagyMeta
  status_counts: StatusCounts
  filters: { search?: string; role?: string }
}

export default function AdminUsersIndex() {
  const { users, pagy, status_counts, filters } = usePage<Props>().props
  const [search, setSearch] = useState(filters.search || "")

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      "/admin/users",
      {
        search: search || undefined,
        role: filters.role || undefined,
      },
      { preserveState: true },
    )
  }

  function handleFilter(role?: string) {
    router.get(
      "/admin/users",
      {
        search: filters.search || undefined,
        role,
      },
      { preserveState: true },
    )
  }

  function handleImpersonate(id: number) {
    router.post("/impersonations", { user_id: id })
  }

  function handleDelete(id: number) {
    if (confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      router.delete(`/admin/users/${id}`)
    }
  }

  return (
    <>
      <Head title="Users - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Users</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <h1 className="text-xl font-bold">Users</h1>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <form onSubmit={handleSearch} className="flex gap-2 sm:max-w-sm">
            <div className="relative flex-1">
              <IconSearch className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>

          <div className="flex items-center gap-2">
            {(
              [
                { label: "All", value: undefined, count: status_counts.all },
                { label: "Admins", value: "admin", count: status_counts.admin },
                { label: "Employees", value: "employee", count: status_counts.employee },
              ] as const
            ).map((filter) => (
              <Button
                key={filter.label}
                variant={filters.role === filter.value ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilter(filter.value)}
              >
                {filter.label} ({filter.count})
              </Button>
            ))}
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Seniority</TableHead>
              <TableHead>Salary</TableHead>
              <TableHead>Time Off</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar size="sm">
                      {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={user.name} /> : null}
                      <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">
                      {user.name}
                      {user.is_current_user && <span className="text-muted-foreground ml-1 text-xs">(You)</span>}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{user.email}</TableCell>
                <TableCell>
                  <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                    {user.role === "admin" ? "Admin" : "Employee"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={user.active ? "default" : "outline"}>{user.active ? "Active" : "Inactive"}</Badge>
                </TableCell>
                <TableCell>
                  {user.seniority_keyword ? (
                    <span>
                      {user.seniority_emoji} {user.seniority_keyword}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </TableCell>
                <TableCell>
                  {user.current_salary_display || <span className="text-muted-foreground">Not set</span>}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {user.balance_count} bal / {user.request_count} req
                </TableCell>
                <TableCell>{formatDate(user.hire_date)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <IconDots className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <a href={`/admin/users/${user.id}`}>
                          <IconEye className="mr-2 h-4 w-4" />
                          View
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a href={`/admin/users/${user.id}/edit`}>
                          <IconPencil className="mr-2 h-4 w-4" />
                          Edit
                        </a>
                      </DropdownMenuItem>
                      {!user.is_current_user && !user.is_admin && (
                        <DropdownMenuItem onClick={() => handleImpersonate(user.id)}>
                          <IconUserShare className="mr-2 h-4 w-4" />
                          Impersonate
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      {user.active ? (
                        <DropdownMenuItem asChild>
                          <a href={`/admin/users/${user.id}#deactivate`}>
                            <IconUserX className="mr-2 h-4 w-4" />
                            Deactivate
                          </a>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem asChild>
                          <a href={`/admin/users/${user.id}#reactivate`}>
                            <IconUserCheck className="mr-2 h-4 w-4" />
                            Reactivate
                          </a>
                        </DropdownMenuItem>
                      )}
                      {!user.is_current_user && (
                        <DropdownMenuItem variant="destructive" onClick={() => handleDelete(user.id)}>
                          <IconTrash className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-muted-foreground text-center">
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

AdminUsersIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
